/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, EventEmitter, Inject, Input, OnChanges, Optional, Output, Self, SimpleChanges, forwardRef} from '@angular/core';

import {FormControl} from '../../model';
import {NG_ASYNC_VALIDATORS, NG_VALIDATORS} from '../../validators';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '../control_value_accessor';
import {NgControl} from '../ng_control';
import {ReactiveErrors} from '../reactive_errors';
import {composeAsyncValidators, composeValidators, isPropertyUpdated, selectValueAccessor, setUpControl} from '../shared';
import {AsyncValidator, AsyncValidatorFn, Validator, ValidatorFn} from '../validators';

export const /** @type {?} */ formControlBinding: any = {
  provide: NgControl,
  useExisting: forwardRef(() => FormControlDirective)
};
/**
 * \@whatItDoes Syncs a standalone {\@link FormControl} instance to a form control element.
 * 
 * In other words, this directive ensures that any values written to the {\@link FormControl}
 * instance programmatically will be written to the DOM element (model -> view). Conversely,
 * any values written to the DOM element through user input will be reflected in the
 * {\@link FormControl} instance (view -> model).
 * 
 * \@howToUse 
 * 
 * Use this directive if you'd like to create and manage a {\@link FormControl} instance directly.
 * Simply create a {\@link FormControl}, save it to your component class, and pass it into the
 * {\@link FormControlDirective}.
 * 
 * This directive is designed to be used as a standalone control.  Unlike {\@link FormControlName},
 * it does not require that your {\@link FormControl} instance be part of any parent
 * {\@link FormGroup}, and it won't be registered to any {\@link FormGroupDirective} that
 * exists above it.
 * 
 * **Get the value**: the `value` property is always synced and available on the
 * {\@link FormControl} instance. See a full list of available properties in
 * {\@link AbstractControl}.
 * 
 * **Set the value**: You can pass in an initial value when instantiating the {\@link FormControl},
 * or you can set it programmatically later using {\@link AbstractControl#setValue} or
 * {\@link AbstractControl#patchValue}.
 * 
 * **Listen to value**: If you want to listen to changes in the value of the control, you can
 * subscribe to the {\@link AbstractControl#valueChanges} event.  You can also listen to
 * {\@link AbstractControl#statusChanges} to be notified when the validation status is
 * re-calculated.
 * 
 * ### Example
 * 
 * {\@example forms/ts/simpleFormControl/simple_form_control_example.ts region='Component'}
 * 
 * * **npm package**: `\@angular/forms`
 * 
 * * **NgModule**: `ReactiveFormsModule`
 * 
 *  \@stable
 */
export class FormControlDirective extends NgControl implements OnChanges {
  viewModel: any;

   form: FormControl;
   model: any;
   update = new EventEmitter();
/**
 * @param {?} isDisabled
 * @return {?}
 */
set isDisabled(isDisabled: boolean) { ReactiveErrors.disabledAttrWarning(); }
/**
 * @param {?} validators
 * @param {?} asyncValidators
 * @param {?} valueAccessors
 */
constructor(   validators: Array<Validator|ValidatorFn>,
                 asyncValidators: Array<AsyncValidator|AsyncValidatorFn>,
                
              valueAccessors: ControlValueAccessor[]) {
                super();
                this._rawValidators = validators || [];
                this._rawAsyncValidators = asyncValidators || [];
                this.valueAccessor = selectValueAccessor(this, valueAccessors);
              }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
                if (this._isControlChanged(changes)) {
                  setUpControl(this.form, this);
                  if (this.control.disabled && /** @type {?} */(( this.valueAccessor)).setDisabledState) { /** @type {?} */(( /** @type {?} */((
                    this.valueAccessor)).setDisabledState))(true);
                  }
                  this.form.updateValueAndValidity({emitEvent: false});
                }
                if (isPropertyUpdated(changes, this.viewModel)) {
                  this.form.setValue(this.model);
                  this.viewModel = this.model;
                }
              }
/**
 * @return {?}
 */
get path(): string[] { return []; }
/**
 * @return {?}
 */
get validator(): ValidatorFn|null { return composeValidators(this._rawValidators); }
/**
 * @return {?}
 */
get asyncValidator(): AsyncValidatorFn|null {
                return composeAsyncValidators(this._rawAsyncValidators);
              }
/**
 * @return {?}
 */
get control(): FormControl { return this.form; }
/**
 * @param {?} newValue
 * @return {?}
 */
viewToModelUpdate(newValue: any): void {
                this.viewModel = newValue;
                this.update.emit(newValue);
              }
/**
 * @param {?} changes
 * @return {?}
 */
private _isControlChanged(changes: {[key: string]: any}): boolean {
                return changes.hasOwnProperty('form');
              }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: '[formControl]', providers: [formControlBinding], exportAs: 'ngForm'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Array, decorators: [{ type: Optional }, { type: Self }, { type: Inject, args: [NG_VALIDATORS, ] }, ]},
{type: Array, decorators: [{ type: Optional }, { type: Self }, { type: Inject, args: [NG_ASYNC_VALIDATORS, ] }, ]},
{type: Array, decorators: [{ type: Optional }, { type: Self }, { type: Inject, args: [NG_VALUE_ACCESSOR, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'form': [{ type: Input, args: ['formControl', ] },],
'model': [{ type: Input, args: ['ngModel', ] },],
'update': [{ type: Output, args: ['ngModelChange', ] },],
'isDisabled': [{ type: Input, args: ['disabled', ] },],
};
}

function FormControlDirective_tsickle_Closure_declarations() {
/** @type {?} */
FormControlDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FormControlDirective.ctorParameters;
/** @type {?} */
FormControlDirective.propDecorators;
/** @type {?} */
FormControlDirective.prototype.viewModel;
/** @type {?} */
FormControlDirective.prototype.form;
/** @type {?} */
FormControlDirective.prototype.model;
/** @type {?} */
FormControlDirective.prototype.update;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../../packages/forms/src/directives/reactive_directives/form_control_directive.ts